import { css } from '@styled-system/css';
import { useQueryClient } from '@tanstack/react-query';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAnalysisSegment } from '@/features/analysisPage/contexts/AnalysisSegmentContext';
import { useVestiaireLocked } from '@/features/subscription/hooks/useVestiaireAccess';
import { NichesTable } from '@/shared/components/analysis/NichesTable';
import { RelaunchSearchModal } from '@/shared/components/analysis/RelaunchSearchModal';
import { Button } from '@/shared/components/ui/Button';
import { FilterTag } from '@/shared/components/ui/FilterTag';
import { Message } from '@/shared/components/ui/Message';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { useCreateAnalysisFromCriteria, useCreateVestiaireAnalysis } from '@/shared/hooks/analysis/mutations';
import { useGetAnalysisByIdWithCache } from '@/shared/hooks/analysis/useGetAnalysisByIdWithCache';
import { useMoveAnalysisToFolderMutation, useMoveAnalysisToPositionMutation } from '@/shared/hooks/queries/generated';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { computeNicheRowScore } from '@/shared/utils/analysis/nicheScore';
import { hasPlatformRelaunchCriteria, isVestiaireAnalysis } from '@/shared/utils/analysis/platform';
import { mergeSegmentIntoCriteria } from '@/shared/utils/analysis/relaunchCriteria';

import { useAnalysisNiches } from '../hooks/useAnalysisNiches';

import type { AnalysisNiche, FilterValues } from '../types';
import { NicheFilterPopover } from './NicheFilterPopover';
import { NicheSellsHistModal } from './NicheSellsHistModal';

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value < 1 && value !== 0 ? value.toFixed(2) : value.toFixed(0);
};

export interface AnalysisPageNichesProps {
  /** 'vendor' : niches sans score, volumes au mois. */
  variant?: 'product' | 'vendor';
}

export const AnalysisPageNiches: FC<AnalysisPageNichesProps> = ({ variant = 'product' }) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: analysisId } = useParams<{ id: string }>();
  // Segment global : recalcule les CHIFFRES de chaque niche côté serveur.
  // Les filtres locaux ci-dessous ne font que masquer des lignes.
  const { segment } = useAnalysisSegment();
  // Plateforme lue sur l'ANALYSE (requête en cache), jamais sur le contexte de segment : la page
  // publique rend cet onglet HORS AnalysisSegmentProvider, dont la valeur inerte vaut 'vinted'.
  // Tant que l'analyse n'est pas chargée, la plateforme est inconnue et la relance reste masquée.
  const { data: analysisData } = useGetAnalysisByIdWithCache({ id: analysisId ?? '' });
  const analysis = analysisData?.getAnalysisById;
  const platform = analysis ? (analysis.platform ?? 'vinted') : null;
  const { niches, isLoading, error } = useAnalysisNiches(analysisId ?? '', segment);
  const { createAnalysisFromCriteria } = useCreateAnalysisFromCriteria();
  const { createVestiaireAnalysis } = useCreateVestiaireAnalysis();
  // Relance d'une niche VC = création d'une analyse VC : verrouillée sans l'accès du plan (grisée,
  // clic → notification d'abonnement, portée par le bouton verrouillé). Consultation libre.
  const vestiaireLocked = useVestiaireLocked();
  const relaunchLocked = vestiaireLocked && isVestiaireAnalysis({ platform });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [selectedNiche, setSelectedNiche] = useState<AnalysisNiche | null>(null);
  // Niche en attente de relance — le popup configure le nom et rend le coût
  // (1 crédit) explicite avant de lancer
  const [relaunchNiche, setRelaunchNiche] = useState<AnalysisNiche | null>(null);
  const moveToFolderMutation = useMoveAnalysisToFolderMutation();
  const moveToPositionMutation = useMoveAnalysisToPositionMutation();
  const queryClient = useQueryClient();

  const removeFilter = (...keys: (keyof FilterValues)[]) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      keys.forEach((key) => delete newFilters[key]);
      return newFilters;
    });
  };

  const segmentedNiches = niches;

  const filteredNiches = useMemo(
    () =>
      segmentedNiches.filter((niche) => {
        if (searchText && !niche.niche.toLowerCase().includes(searchText.toLowerCase())) return false;
        if (filters.avgPriceMin !== undefined && niche.avgPrice && niche.avgPrice < filters.avgPriceMin) return false;
        if (filters.avgPriceMax !== undefined && niche.avgPrice && niche.avgPrice > filters.avgPriceMax) return false;
        if (filters.productCountMin !== undefined && niche.productCount < filters.productCountMin) return false;
        if (filters.productCountMax !== undefined && niche.productCount > filters.productCountMax) return false;
        if (filters.soldPctMin !== undefined && niche.soldPct && niche.soldPct < filters.soldPctMin) return false;
        if (filters.soldPctMax !== undefined && niche.soldPct && niche.soldPct > filters.soldPctMax) return false;
        if (filters.scoreMin !== undefined || filters.scoreMax !== undefined) {
          const score = computeNicheRowScore(niche);
          if (score === null) return false;
          if (filters.scoreMin !== undefined && score < filters.scoreMin) return false;
          if (filters.scoreMax !== undefined && score > filters.scoreMax) return false;
        }
        if (filters.soldPerDayMin !== undefined && niche.soldPerDay && niche.soldPerDay < filters.soldPerDayMin)
          return false;
        if (filters.soldPerDayMax !== undefined && niche.soldPerDay && niche.soldPerDay > filters.soldPerDayMax)
          return false;
        if (
          filters.timeToSellMin !== undefined &&
          niche.timeToSellHours &&
          niche.timeToSellHours < filters.timeToSellMin
        )
          return false;
        if (
          filters.timeToSellMax !== undefined &&
          niche.timeToSellHours &&
          niche.timeToSellHours > filters.timeToSellMax
        )
          return false;
        if (filters.nicheTypes && filters.nicheTypes.length > 0 && !filters.nicheTypes.includes(niche.type))
          return false;
        return true;
      }),
    [segmentedNiches, searchText, filters],
  );

  const handleExport = () => {
    const headers = [
      t('ui.csv.type'),
      t('niches.colNiche'),
      t('niches.colProducts'),
      t('niches.colAvgPrice'),
      t('niches.colSoldPct'),
      t('ui.csv.soldPerDay'),
      t('ui.csv.addedPerDay'),
      t('ui.csv.timeToSellHours'),
      t('niches.colScore'),
    ];

    const rows = filteredNiches.map((niche) => [
      niche.type === 'brand' ? t('niches.typeBrand') : t('niches.typeCategory'),
      niche.niche,
      niche.productCount.toString(),
      niche.avgPrice ? niche.avgPrice.toFixed(2) : '',
      niche.soldPct ? niche.soldPct.toFixed(2) : '',
      niche.soldPerDay ? niche.soldPerDay.toFixed(2) : '',
      niche.addedPerDay ? niche.addedPerDay.toFixed(2) : '',
      niche.timeToSellHours ? niche.timeToSellHours.toFixed(2) : '',
      computeNicheRowScore(niche)?.toString() ?? '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => (cell.includes(',') || cell.includes('"') ? `"${cell.replace(/"/g, '""')}"` : cell))
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `niches_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Lance la recherche zoom confirmée dans le popup (nom configuré).
  // ZOOM : critères serveur prêts à l'emploi (relaunchCriteria = parent
  // resserré sur la niche), le segment actif resserre encore par-dessus.
  // Le bouton play est masqué quand relaunchCriteria (Vinted ou VC selon la
  // plateforme) est null (cache ancien ou analyse vendeur) — pas de
  // reconstruction de secours.
  const handleConfirmRelaunch = async (name: string, folderId: string | null) => {
    const niche = relaunchNiche;
    if (!niche) return;
    // La mutation se choisit par la plateforme de l'analyse ; le champ de relance de
    // l'AUTRE plateforme est toujours null (bouton masqué si celui de la bonne l'est)
    const result = isVestiaireAnalysis({ platform })
      ? niche.vestiaireRelaunchCriteria
        ? await createVestiaireAnalysis({
            title: name,
            criteria: mergeSegmentIntoCriteria(niche.vestiaireRelaunchCriteria, segment),
            parentAnalysisId: analysisId,
          })
        : null
      : niche.relaunchCriteria
        ? await createAnalysisFromCriteria({
            title: name,
            criteria: mergeSegmentIntoCriteria(niche.relaunchCriteria, segment),
            parentAnalysisId: analysisId,
          })
        : null;
    if (!result) return;
    if (result.success && result.analysis) {
      // Rangement dans le dossier choisi + épinglage EN TÊTE de sa scope —
      // attendu avant de naviguer pour que l'historique reflète le placement
      // au retour (échec non bloquant : la recherche est créée)
      try {
        if (folderId) {
          await moveToFolderMutation.mutateAsync({ input: { analysisId: result.analysis.id, folderId } });
        }
        await moveToPositionMutation.mutateAsync({ input: { analysisId: result.analysis.id, position: 0 } });
        await queryClient.invalidateQueries({ queryKey: ['GetUserAnalysesPaginated', 'catalog'] });
      } catch {
        // le rangement peut se refaire à la main
      }
      navigate(`/analysis/products/${result.analysis.id}`);
    } else if (!result.success) {
      // Cas notable : imageKey périmée reportée dans relaunchCriteria → 400
      // « aucune image n'a pu être chargée » — le popup affiche le message.
      throw new Error(result.error ?? t('niches.relaunchError'));
    }
  };

  if (error) {
    return (
      <Message type="error" bordered>
        {error}
      </Message>
    );
  }

  return (
    <div className={css({ display: 'flex', flexDirection: 'column', gap: '5' })}>
      {/* ─── Toolbar ─────────────────────────────────────────────────── */}
      <div className={css({ display: 'flex', gap: '3', alignItems: 'center', flexWrap: 'wrap' })}>
        <SearchBarNew placeholder={t('niches.searchPlaceholder')} value={searchText} onChange={setSearchText} />

        {!isMobile && (
          <NicheFilterPopover filters={filters} onFilterChange={setFilters} showScore={variant === 'product'} />
        )}

        {(filters.avgPriceMin !== undefined || filters.avgPriceMax !== undefined) && (
          <FilterTag
            name={t('niches.colAvgPrice')}
            value={
              filters.avgPriceMin !== undefined && filters.avgPriceMax !== undefined
                ? `${filters.avgPriceMin} € - ${filters.avgPriceMax} €`
                : filters.avgPriceMin !== undefined
                  ? `${filters.avgPriceMin} € min.`
                  : `${filters.avgPriceMax} € max.`
            }
            onRemove={() => removeFilter('avgPriceMin', 'avgPriceMax')}
          />
        )}

        {(filters.productCountMin !== undefined || filters.productCountMax !== undefined) && (
          <FilterTag
            name={t('niches.colProducts')}
            value={
              filters.productCountMin !== undefined && filters.productCountMax !== undefined
                ? `${filters.productCountMin} - ${filters.productCountMax}`
                : filters.productCountMin !== undefined
                  ? `${filters.productCountMin} min.`
                  : `${filters.productCountMax} max.`
            }
            onRemove={() => removeFilter('productCountMin', 'productCountMax')}
          />
        )}

        {(filters.soldPctMin !== undefined || filters.soldPctMax !== undefined) && (
          <FilterTag
            name={t('niches.colSoldPct')}
            value={
              filters.soldPctMin !== undefined && filters.soldPctMax !== undefined
                ? `${filters.soldPctMin} % - ${filters.soldPctMax} %`
                : filters.soldPctMin !== undefined
                  ? `${filters.soldPctMin} % min.`
                  : `${filters.soldPctMax} % max.`
            }
            onRemove={() => removeFilter('soldPctMin', 'soldPctMax')}
          />
        )}

        {(filters.scoreMin !== undefined || filters.scoreMax !== undefined) && (
          <FilterTag
            name={t('niches.colScore')}
            value={
              filters.scoreMin !== undefined && filters.scoreMax !== undefined
                ? `${filters.scoreMin} - ${filters.scoreMax}`
                : filters.scoreMin !== undefined
                  ? `${filters.scoreMin} min.`
                  : `${filters.scoreMax} max.`
            }
            onRemove={() => removeFilter('scoreMin', 'scoreMax')}
          />
        )}

        {(filters.soldPerDayMin !== undefined || filters.soldPerDayMax !== undefined) && (
          <FilterTag
            name={t('niches.colSoldPerDay')}
            value={
              filters.soldPerDayMin !== undefined && filters.soldPerDayMax !== undefined
                ? `${formatNumber(filters.soldPerDayMin)} - ${formatNumber(filters.soldPerDayMax)}`
                : filters.soldPerDayMin !== undefined
                  ? `${formatNumber(filters.soldPerDayMin)} min.`
                  : `${formatNumber(filters.soldPerDayMax!)} max.`
            }
            onRemove={() => removeFilter('soldPerDayMin', 'soldPerDayMax')}
          />
        )}

        {(filters.timeToSellMin !== undefined || filters.timeToSellMax !== undefined) && (
          <FilterTag
            name={t('niches.colTimeToSell')}
            value={
              filters.timeToSellMin !== undefined && filters.timeToSellMax !== undefined
                ? `${filters.timeToSellMin}h - ${filters.timeToSellMax}h`
                : filters.timeToSellMin !== undefined
                  ? `${filters.timeToSellMin}h min.`
                  : `${filters.timeToSellMax}h max.`
            }
            onRemove={() => removeFilter('timeToSellMin', 'timeToSellMax')}
          />
        )}

        {filters.nicheTypes !== undefined && filters.nicheTypes.length > 0 && (
          <FilterTag
            name={t('niches.typeFilter')}
            value={filters.nicheTypes
              .map((tp) => (tp === 'brand' ? t('niches.typeBrand') : t('niches.typeCategory')))
              .join(', ')}
            onRemove={() => removeFilter('nicheTypes')}
          />
        )}

        <Button icon="download" handleClick={handleExport} className={css({ ml: 'auto' })}>
          {t('niches.export')}
        </Button>
      </div>

      {/* Les filtres locaux masquent des lignes, ils ne changent pas les
          chiffres du périmètre : on le dit explicitement. */}
      {filteredNiches.length < segmentedNiches.length && (
        <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
          {t('niches.shownCount', { shown: filteredNiches.length, total: segmentedNiches.length })}
        </span>
      )}

      {/* ─── Tableau DS ──────────────────────────────────────────────── */}
      <NichesTable
        niches={filteredNiches}
        loading={isLoading}
        variant={variant}
        onViewSales={(niche) => setSelectedNiche(niche)}
        platform={platform}
        onLaunchSearch={(niche) => setRelaunchNiche(niche)}
        launchLocked={relaunchLocked}
      />

      {/* ─── Drill-down ventes de la niche ───────────────────────────── */}
      {selectedNiche && (
        <NicheSellsHistModal
          analysisId={analysisId ?? ''}
          brandOrFullPath={selectedNiche.niche}
          nicheImageUrl={selectedNiche.imageUrl ?? undefined}
          niche={selectedNiche.niche}
          allNiches={segmentedNiches}
          onClose={() => setSelectedNiche(null)}
          onLaunchSearch={
            platform !== null && hasPlatformRelaunchCriteria(selectedNiche, platform)
              ? () => setRelaunchNiche(selectedNiche)
              : undefined
          }
          launchLocked={relaunchLocked}
        />
      )}

      {/* ─── Confirmation de la relance (nom + coût 1 crédit) ────────── */}
      <RelaunchSearchModal
        isOpen={relaunchNiche !== null}
        defaultName={relaunchNiche?.niche ?? ''}
        onClose={() => setRelaunchNiche(null)}
        onConfirm={handleConfirmRelaunch}
      />
    </div>
  );
};
